import React, { useEffect, useRef, useState } from 'react';
import { useNavigation } from '../../../utils/redirectUtils';
import { trackPostHogEvent } from '../../../utils/posthogUtils';
import { startInterview } from '../../../utils/interviewFlow';
import Pricing from '../../../data/Pricing';
import styles from './CEOHero.module.css';
import AsSeenOn from '../../.shared/AsSeenOn/AsSeenOn';
import paulHeroImg from '../../../assets/ceo/paul-hero.png';

function CEOHero({ safeSource, switchAndSaveSource }) {
    const [isVisible, setIsVisible] = useState(false);
    const [startingPrice, setStartingPrice] = useState('$124');
    const elementRef = useRef(null);
    const navigate = useNavigation();

    const handleGetStarted = () => {
        // Enroll before tracking, so trackExposure has registered the variant
        // on this person and the submit event below carries it.
        const flow = startInterview();

        trackPostHogEvent('landing_page_submitted', {
            selected: 'get_started',
            to_path: '/interview',
            interview_flow: flow.id
        });
        navigate(flow.entryPath);
    };

    const handleScrollToVideo = (e) => {
        e.preventDefault();
        const target = document.getElementById('ceo-video');
        if (target) {
            const top = target.getBoundingClientRect().top + window.pageYOffset - 70;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    useEffect(() => {
        const loadPrices = async () => {
            try {
                const pricing = Pricing();
                const priceData = await pricing.getPrices();
                if (priceData) {
                    // "Starting at" is the lowest per-month figure, i.e. the 3-month plan's
                    // monthly equivalent. Plans expose `monthly_amount`/`total` — there is no `price` key.
                    const sema = priceData.semaglutide?.injectable?.threeMonths;
                    if (sema?.monthly_amount) setStartingPrice(`$${sema.monthly_amount.split('.')[0]}`);
                }
            } catch {
                // fallback to default
            }
        };
        loadPrices();

        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
            { threshold: 0.3 }
        );
        const el = elementRef.current;
        if (el) observer.observe(el);
        return () => { if (el) observer.unobserve(el); };
    }, []);

    const animClass = isVisible ? ' animate__animated animate__fadeInUp' : '';

    return (
        <div className={styles.heroWrap}>
            <section className={styles.hero}>
                <div className={styles.heroImg}>
                    <img src={paulHeroImg} alt="Paul Moss, CEO & Co-Founder of TB2 Health" />
                </div>
                <div ref={elementRef} className={styles.heroText}>
                    <div className={styles.heroEyebrow + animClass}>
                        <span className={styles.eyebrowA}>Founded from experience</span>
                        <span className={styles.eyebrowB}>Paul's story</span>
                    </div>
                    <h1 className={styles.heroH1 + animClass}>
                        Our CEO On <span className={styles.grad}>His Struggle</span>
                    </h1>
                    <p className={styles.heroSub + animClass}>
                        Why weight loss is difficult &nbsp;~&nbsp; How to use GLP-1s effectively.
                    </p>
                    <p className={styles.heroSub2 + animClass}>
                        TB2 Health was founded because GLP-1s changed Paul's life — and too many people couldn't
                        access them or didn't have the right information. Now you can.
                    </p>
                    <div className={styles.buttonContainer + animClass}>
                        <button className={styles.heroCta} onClick={handleGetStarted}>
                            Get Started Today
                        </button>
                    </div>
                    <div className={styles.priceLine + animClass}>
                        <div>Starting at {startingPrice}<span className={styles.month}>/month</span></div>
                        <span className={styles.cancel}>Cancel anytime</span>
                    </div>
                </div>
            </section>

            <AsSeenOn />

            <a className={styles.scrollHint} href="#ceo-video" onClick={handleScrollToVideo} aria-label="Watch Paul's story">
                <div className={styles.scrollHintLabel}>
                    <span className={styles.scrollHintPlay}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><polygon points="5,3 19,12 5,21" /></svg>
                    </span>
                    Watch Paul's 13-minute story below
                </div>
                <div className={styles.scrollHintArrow}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </div>
            </a>
        </div>
    );
}

export default CEOHero;
